/**
 * Sidebar, mobile sheet and command palette navigation: the single source.
 *
 * Frozen by S0 (playbook 14 sections 4.3 and 8.1, plan 01 section 4.2). Streams
 * read this file; nobody edits the items. Stream A renders it (Sidebar,
 * MobileSidebar, CommandPalette) and builds breadcrumbs in `lib/nav-paths.ts`
 * from `labelForPath`, `activeHref` and `ROOT_LABEL` below.
 *
 * This file imports no other `lib/` module so `nav-paths.ts` and `roles.ts`
 * can import it without a cycle.
 */
import type { ComponentType } from 'react'
import { Receipt } from 'lucide-react'
import { Icons } from '@/components/ui/icons'

/** Product line under "Supervity" in the rail (rendered uppercase). */
export const AUTO_NAME = process.env.NEXT_PUBLIC_AUTO_NAME || 'Accounts Receivable'

/** Second product line under `AUTO_NAME` (rendered uppercase). */
export const PRODUCT_LINE = 'Command Center'

/** Root crumb label. */
export const ROOT_LABEL = 'Command Briefing'

/**
 * A live count on a nav entry. The key names the source; the sidebar resolves
 * it (`useNavBadges`), so this file stays static data.
 */
export type NavBadgeKey = 'explorer-review' | 'helpdesk-open'

export interface NavItem {
  href: string
  label: string
  icon: ComponentType<{ className?: string; strokeWidth?: number }>
  /** Extra path prefixes that light this item (longest match wins across all items). */
  match?: string[]
  badge?: NavBadgeKey
}

export interface NavSection {
  title: string
  items: NavItem[]
  /** Folded until opened. The heading stays; one click opens the section. */
  collapsedByDefault?: boolean
}

export const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Platform',
    items: [
      { href: '/', label: 'Command Briefing', icon: Icons.sparkles },
      { href: '/dashboard', label: 'Dashboard', icon: Icons.dashboard },
      {
        href: '/receivables',
        label: 'Explorer',
        icon: Receipt,
        match: ['/workbench', '/invoices'],
        badge: 'explorer-review',
      },
      {
        href: '/queries',
        label: 'Help Desk',
        icon: Icons.help,
        match: ['/help-desk'],
        badge: 'helpdesk-open',
      },
      { href: '/portal', label: 'Customer Portal', icon: Icons.globe },
      { href: '/agent-hub', label: 'Agent Hub', icon: Icons.cpu },
      { href: '/data-manager', label: 'Data Manager', icon: Icons.layers },
    ],
  },
  {
    title: 'Requests',
    items: [
      { href: '/queries/billing', label: 'Billing Requests', icon: Icons.banknote },
      { href: '/queries/master-data', label: 'Master Data Requests', icon: Icons.database },
      { href: '/queries/asset', label: 'Asset Requests', icon: Icons.building },
    ],
  },
  {
    title: 'AI Intelligence',
    items: [
      { href: '/ai/policies', label: 'AI Policies', icon: Icons.brain },
      // The page has two views: Insights and Auto Graph (`graphHref()`).
      { href: '/ai/insights', label: 'AI Insights', icon: Icons.lightbulb },
    ],
  },
  {
    title: 'Administration',
    collapsedByDefault: true,
    items: [
      { href: '/admin/users', label: 'User Management', icon: Icons.users },
      { href: '/admin/roles', label: 'Role Management', icon: Icons.shield },
      { href: '/admin/groups', label: 'Group Management', icon: Icons.network },
      { href: '/admin/sessions', label: 'User Sessions', icon: Icons.device },
      { href: '/admin/events', label: 'Login Events', icon: Icons.activity },
      { href: '/admin/audit', label: 'Audit Logs', icon: Icons.fileText },
      { href: '/admin/settings', label: 'Admin Settings', icon: Icons.settings },
    ],
  },
  {
    title: 'System',
    collapsedByDefault: true,
    items: [{ href: '/settings', label: 'Settings', icon: Icons.settings }],
  },
]

/** Labels of the AI Insights page views (decision D2: the graph is "Auto Graph"). */
export const INSIGHTS_VIEW_LABELS = { insights: 'Insights', graph: 'Auto Graph' } as const

/** Default access rule: `/admin/*` needs the `admin` role. `lib/roles.ts` may pass a richer one. */
export function defaultCanAccess(href: string, roles?: readonly string[] | null): boolean {
  if (href === '/admin' || href.startsWith('/admin/')) return !!roles?.includes('admin')
  return true
}

/** Sections filtered to what `roles` may open; empty sections are dropped. */
export function visibleNavSections(
  roles?: readonly string[] | null,
  canAccess: (href: string, roles?: readonly string[] | null) => boolean = defaultCanAccess,
): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => canAccess(item.href, roles)),
  })).filter((section) => section.items.length > 0)
}

/** Every nav item, in rail order. */
export function allNavItems(): NavItem[] {
  return NAV_SECTIONS.flatMap((section) => section.items)
}

/** The nav label for an exact href, or undefined when no entry has that href. */
export function labelForPath(path: string): string | undefined {
  return allNavItems().find((item) => item.href === path)?.label
}

function prefixMatches(pathname: string, prefix: string): boolean {
  if (prefix === '/') return pathname === '/'
  return pathname === prefix || pathname.startsWith(`${prefix}/`)
}

/**
 * The single nav href that should light for `pathname`: the item whose href or
 * `match` prefix is the longest match. `/queries/billing/12` lights Billing
 * Requests, not Help Desk; `/workbench` lights Explorer; `/` lights only on `/`.
 */
export function activeHref(pathname: string | null | undefined, items: NavItem[] = allNavItems()): string | undefined {
  if (!pathname) return undefined
  let best: { href: string; length: number } | undefined
  for (const item of items) {
    for (const prefix of [item.href, ...(item.match ?? [])]) {
      if (prefixMatches(pathname, prefix) && (!best || prefix.length > best.length)) {
        best = { href: item.href, length: prefix.length }
      }
    }
  }
  return best?.href
}
